import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useTranslation } from 'react-i18next';

type Prefs = {
  analyticsConsent: boolean;
  pushConsent: boolean;
  locationConsent: boolean;
};

const defaultPrefs: Prefs = {
  analyticsConsent: false,
  pushConsent: false,
  locationConsent: false,
};

const ToggleRow: React.FC<{
  title: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  yesLabel: string;
  noLabel: string;
}> = ({ title, description, checked, onChange, yesLabel, noLabel }) => {
  return (
    <div style={row}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>{title}</div>
        {description ? <div style={desc}>{description}</div> : null}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        aria-pressed={checked}
        style={{
          ...toggleBtn,
          background: checked ? '#10B981' : '#EF4444', // green / red
        }}
      >
        {checked ? yesLabel : noLabel}
      </button>
    </div>
  );
};

const RequirePrivacyChangeMap: React.FC = () => {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  // kept in case you still want to read it later; not used for routing anymore
  const _returnTo = sp.get('return') || '/gallery';
  const { t } = useTranslation();

  const [prefs, setPrefs] = useState<Prefs>(defaultPrefs);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Load current preferences (fallback to defaults)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/privacy/preferences');
        setPrefs({ ...defaultPrefs, ...data });
      } catch {
        setPrefs(defaultPrefs);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setKey =
    (key: keyof Prefs) =>
    (v: boolean) =>
      setPrefs((p) => ({ ...p, [key]: v }));

  // ✅ Continue -> save then go to /change-location-geo or /change-location-nogeo
  const handleContinue = async () => {
    try {
      setSaving(true);
      setError('');
      await api.put('/privacy/preferences', { ...prefs });

      if (prefs.locationConsent) {
        navigate('/change-location-geo', { replace: true });
      } else {
        navigate('/change-location-nogeo', { replace: true });
      }
    } catch (e: any) {
      setError(
        e?.response?.data?.msg || t('requirePrivacyChangeMap.errorSave')
      );
    } finally {
      setSaving(false);
    }
  };

  // ✅ Close -> keep user on the non-geo page
  const handleClose = () => {
    navigate('/change-location-nogeo', { replace: true });
  };

  return (
    <div
      style={overlayWrap}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rpc-title"
      onKeyDown={(e) => {
        if (e.key === 'Escape') handleClose();
      }}
      tabIndex={-1}
    >
      <div style={panel}>
        {/* Close button (round, subtle, better hit area) */}
        <button
          type="button"
          onClick={handleClose}
          aria-label={t('requirePrivacyChangeMap.close')}
          style={closeIconBtn}
          onMouseEnter={(e) =>
            (e.currentTarget.style.borderColor = '#D4AF37')
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)')
          }
          onFocus={(e) =>
            (e.currentTarget.style.borderColor = '#D4AF37')
          }
          onBlur={(e) =>
            (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)')
          }
        >
          ✕
        </button>

        <h2 id="rpc-title" style={title}>
          {t('requirePrivacyChangeMap.title')}
        </h2>

        <div style={card}>
          {loading ? (
            <p>{t('firstRunPrivacy.loading')}</p>
          ) : (
            <>
              <ToggleRow
                title={t('firstRunPrivacy.toggles.locationTitle')}
                description={t('firstRunPrivacy.toggles.locationDesc')}
                checked={prefs.locationConsent}
                onChange={setKey('locationConsent')}
                yesLabel={t('firstRunPrivacy.yes')}
                noLabel={t('firstRunPrivacy.no')}
              />
              <ToggleRow
                title={t('firstRunPrivacy.toggles.analyticsTitle')}
                description={t('firstRunPrivacy.toggles.analyticsDesc')}
                checked={prefs.analyticsConsent}
                onChange={setKey('analyticsConsent')}
                yesLabel={t('firstRunPrivacy.yes')}
                noLabel={t('firstRunPrivacy.no')}
              />
              <ToggleRow
                title={t('firstRunPrivacy.toggles.pushTitle')}
                description={t('firstRunPrivacy.toggles.pushDesc')}
                checked={prefs.pushConsent}
                onChange={setKey('pushConsent')}
                yesLabel={t('firstRunPrivacy.yes')}
                noLabel={t('firstRunPrivacy.no')}
              />

              {error && <div style={errBox}>{error}</div>}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" onClick={handleClose} style={secondaryBtn}>
                  {t('requirePrivacyChangeMap.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={saving}
                  style={continueBtn}
                >
                  {saving
                    ? t('firstRunPrivacy.saving')
                    : t('firstRunPrivacy.continue')}
                </button>
              </div>

              <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
                {t('firstRunPrivacy.footerNote')}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ——— styles ——— */
const overlayWrap: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.35)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '60px 16px',
  zIndex: 9999,
};

const panel: React.CSSProperties = {
  width: '100%',
  maxWidth: 760,
  color: '#111',
  background: 'transparent',
  position: 'relative',
};

const title: React.CSSProperties = {
  fontSize: 24,
  margin: '0 0 14px 0',
  color: '#fff',
};

const card: React.CSSProperties = {
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: 12,
  padding: 16,
  background: 'rgba(255,255,255,0.98)',
  boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
};

const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 0',
  borderBottom: '1px solid #f1f5f9',
};

const desc: React.CSSProperties = { color: '#6b7280', fontSize: 13, marginTop: 4 };

const toggleBtn: React.CSSProperties = {
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  padding: '8px 14px',
  cursor: 'pointer',
  minWidth: 70,
  fontWeight: 600,
};

const continueBtn: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 8,
  border: '1px solid #4b2a8d',
  color: '#fff',
  background: 'rgb(56, 6, 117)',
  cursor: 'pointer',
  minWidth: 140,
};

const secondaryBtn: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  color: '#111',
  background: '#fff',
  cursor: 'pointer',
  minWidth: 100,
};

const errBox: React.CSSProperties = {
  background: '#fff7ed',
  border: '1px solid #fdba74',
  color: '#7c2d12',
  padding: 10,
  borderRadius: 8,
  marginTop: 10,
};

const closeIconBtn: React.CSSProperties = {
  position: 'absolute',
  right: 0,
  top: -48,
  width: 36,
  height: 36,
  borderRadius: '9999px',
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'rgba(0,0,0,0.55)',
  color: '#e5e7eb',
  cursor: 'pointer',
  lineHeight: '34px',
  textAlign: 'center' as const,
  fontSize: 16,
  backdropFilter: 'blur(2px)',
};

export default RequirePrivacyChangeMap;
